import type { AssistantProjectMediaAnalysis } from '@/features/assistant/tools/media-tools'

function formatPlannerMediaContext(analysis: AssistantProjectMediaAnalysis): string {
  const usedVisualItems = analysis.items
    .filter((item) => item.isUsedOnTimeline && (item.kind === 'video' || item.kind === 'image'))
    .slice()
    .sort((left, right) => right.timelineItemCount - left.timelineItemCount || right.durationSeconds - left.durationSeconds)
    .slice(0, 5)
    .map((item, index) =>
      `${index + 1}. ${item.fileName} kind=${item.kind} duration=${Math.round(item.durationSeconds)}s timeline=${item.timelineItemCount}x`,
    )

  const planningHints: string[] = []
  if (analysis.media.usedOnTimelineCount > 0) {
    planningHints.push(
      'Prefer reusing assets that are already on the timeline before introducing newly generated reference visuals.',
    )
  }
  if (analysis.media.transcriptReadyCount > 0) {
    planningHints.push('Transcript-ready media exists, so captions/transcript reuse may be possible later.')
  }
  if (analysis.media.videoCount + analysis.media.imageCount === 0) {
    planningHints.push('No visual media is currently available, so generated references may be necessary.')
  }

  const topItems = analysis.items
    .slice()
    .sort((left, right) => {
      if (left.isUsedOnTimeline !== right.isUsedOnTimeline) {
        return Number(right.isUsedOnTimeline) - Number(left.isUsedOnTimeline)
      }
      return right.durationSeconds - left.durationSeconds
    })
    .slice(0, 8)
    .map((item, index) => {
      const tags = item.tags.length > 0 ? ` tags=${item.tags.join('|')}` : ''
      return [
        `${index + 1}. ${item.fileName}`,
        `kind=${item.kind}`,
        `duration=${Math.round(item.durationSeconds)}s`,
        `transcript=${item.hasTranscript ? 'yes' : 'no'}`,
        `timeline=${item.isUsedOnTimeline ? `${item.timelineItemCount}x` : 'no'}`,
        tags,
      ].join(' ')
    })

  return [
    'Project context:',
    `- project: ${analysis.project.name}`,
    `- canvas: ${analysis.project.width}x${analysis.project.height} @ ${analysis.project.fps}fps`,
    `- media: total=${analysis.media.totalItems}, video=${analysis.media.videoCount}, audio=${analysis.media.audioCount}, image=${analysis.media.imageCount}, transcriptReady=${analysis.media.transcriptReadyCount}, usedOnTimeline=${analysis.media.usedOnTimelineCount}, totalDuration=${Math.round(analysis.media.totalDurationSeconds)}s`,
    `- timeline: tracks=${analysis.timeline.trackCount}, items=${analysis.timeline.itemCount}, transitions=${analysis.timeline.transitionCount}, referencedMedia=${analysis.timeline.referencedMediaCount}, totalDuration=${Math.round(analysis.timeline.totalDurationSeconds)}s`,
    usedVisualItems.length > 0 ? '- timeline visuals already in use:' : '- timeline visuals already in use: none',
    ...usedVisualItems.map((item) => `  ${item}`),
    topItems.length > 0 ? '- media items:' : '- media items: none',
    ...topItems.map((item) => `  ${item}`),
    planningHints.length > 0 ? '- planning hints:' : '- planning hints: none',
    ...planningHints.map((hint) => `  ${hint}`),
    'Use this context when choosing duration, structure, and whether to reuse existing assets.',
  ].join('\n')
}

export function buildDeepSeekPlannerSystemPrompt(): string {
  return [
    'You are the FastCut internal assistant planner.',
    'Your job is to transform one user request into exactly one JSON object.',
    'Do not output markdown, code fences, explanations, or extra text.',
    'Only output valid JSON matching this schema:',
    '{',
    '  "taskType": "voiceover_video",',
    '  "topic": string,',
    '  "script": string,',
    '  "durationSeconds": number,',
    '  "aspectRatio": "9:16" | "16:9" | "1:1",',
    '  "tone": "natural" | "professional" | "friendly" | "energetic" | "calm",',
    '  "needsTts": boolean,',
    '  "needsCaptions": boolean,',
    '  "needsBgm": boolean,',
    '  "exportDraft": boolean',
    '}',
    'Assume taskType is always voiceover_video for this phase.',
    'Infer missing fields conservatively using these defaults:',
    '- durationSeconds: 45',
    '- aspectRatio: 9:16',
    '- tone: natural',
    '- needsTts: true',
    '- needsCaptions: true',
    '- needsBgm: true',
    '- exportDraft: false',
    'Set topic to a concise Chinese summary of the user goal, suitable as a title.',
    'Set script to a complete Chinese voiceover draft, not a title or outline.',
    'The script should sound natural when spoken and should roughly match the requested duration.',
    'Aim for about 4 to 6 Chinese characters per second of requested duration unless the user asks for a different density.',
    'Do not make script identical to topic.',
  ].join('\n')
}

export function buildDeepSeekPlannerUserPrompt(
  userPrompt: string,
  projectMediaAnalysis?: AssistantProjectMediaAnalysis,
): string {
  const sections = ['User request:', userPrompt.trim()]

  if (projectMediaAnalysis) {
    sections.push('', formatPlannerMediaContext(projectMediaAnalysis))
  }

  return sections.join('\n')
}
